import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Text,
  Box,
  HStack,
  VStack,
  Icon,
  Badge,
  Image,
  useColorModeValue,
  useBreakpointValue,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaDownload, FaCalendarAlt, FaUser, FaFileAudio, FaFileVideo, FaFileCode, FaFileAlt } from 'react-icons/fa';

const DocumentViewerModal = ({ isOpen, onClose, fileData, parallelMode = false }) => {
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Definir todos los valores de color que se usan condicionalmente
  const imageContainerBg = useColorModeValue("gray.50", "gray.700");
  const fallbackMessageBg = useColorModeValue("gray.50", "gray.700");
  const fallbackBorderColor = useColorModeValue("gray.300", "gray.500");

  // Responsive breakpoints para decidir posicionamiento
  const isMobileOrTablet = useBreakpointValue({ base: true, lg: false });
  const isVerticalLayout = parallelMode && isMobileOrTablet;


  if (!fileData) return null;

  let filename, fileUrl, fileNameOnly, fileFormat;
  
  try {
    // Si ya viene procesado desde Files.js
    if (fileData.fileUrl && fileData.fileName) {
      fileUrl = fileData.fileUrl;
      fileNameOnly = fileData.fileName;
      filename = fileData.filename;
    } else {
      // Parsing original para compatibilidad
      const parsedFilenames = JSON.parse(fileData.filenames);
      filename = parsedFilenames[0];
      const cleanFilename = filename.replace(/\\/g, '');
      fileUrl = `${process.env.REACT_APP_SURVEY_API_URL || 'https://datofy.cl'}${cleanFilename}`;
      fileNameOnly = cleanFilename.split("/").pop();
    }
    fileFormat = fileNameOnly.split(".").pop().toLowerCase();
  } catch (error) {
    console.error('Error processing file data:', error);
    filename = '';
    fileUrl = '';
    fileNameOnly = 'Archivo';
    fileFormat = 'unknown';
  }
  
  const createdAt = new Date(fileData.created_at).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = (format) => {
    switch (format) {
      case 'pdf':
        return { icon: FaFilePdf, color: 'red.500' };

      // Imágenes
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp':
      case 'svg':
      case 'ico':
      case 'tiff':
        return { icon: FaFileImage, color: 'green.500' };

      // Documentos Office
      case 'doc':
      case 'docx':
        return { icon: FaFileWord, color: 'blue.500' };
      case 'xls':
      case 'xlsx':
        return { icon: FaFileExcel, color: 'green.600' };

      // Audio
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'aac':
      case 'm4a':
      case 'flac':
        return { icon: FaFileAudio, color: 'purple.500' };

      // Video
      case 'mp4':
      case 'webm':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
      case 'm4v':
        return { icon: FaFileVideo, color: 'orange.500' };

      // Código y texto
      case 'js':
      case 'css':
      case 'php':
      case 'py':
      case 'java':
      case 'cpp':
      case 'c':
      case 'h':
      case 'sql':
      case 'yaml':
      case 'yml':
        return { icon: FaFileCode, color: 'cyan.500' };

      // Texto plano y documentos
      case 'txt':
      case 'csv':
      case 'json':
      case 'xml':
      case 'html':
      case 'htm':
      case 'md':
      case 'log':
        return { icon: FaFileAlt, color: 'gray.600' };

      default:
        return { icon: FaFile, color: 'gray.500' };
    }
  };

  const { icon: FileIcon, color } = getFileIcon(fileFormat);

  // Función para renderizar el contenido del archivo
  const renderFileContent = () => {
    // Imágenes - render con componente Image de Chakra
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff'].includes(fileFormat)) {
      return (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          overflow="hidden"
          bg={imageContainerBg}
          p={4}
        >
          <Image
            src={fileUrl}
            alt={fileNameOnly}
            maxW="100%"
            maxH={
              isVerticalLayout ? "180px" :
              parallelMode ? "350px" : "600px"
            }
            objectFit="contain"
            fallback={
              <Center h="400px">
                <Spinner size="xl" />
              </Center>
            }
          />
        </Box>
      );
    }
    // PDFs y documentos que el navegador puede renderizar nativamente
    else if (['pdf', 'txt', 'csv', 'json', 'xml', 'html', 'htm'].includes(fileFormat)) {
      return (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          overflow="hidden"
          bg={imageContainerBg}
        >
          <iframe
            src={fileUrl}
            width="100%"
            height={
              isVerticalLayout ? "200px" :
              parallelMode ? "450px" : "600px"
            }
            title={fileNameOnly}
            style={{ border: 'none' }}
          />
        </Box>
      );
    }
    // Audio files - render con elemento audio nativo
    else if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(fileFormat)) {
      return (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          overflow="hidden"
          bg={imageContainerBg}
          p={8}
          textAlign="center"
        >
          <Icon as={FileIcon} boxSize={16} color={color} mb={6} />
          <Text fontSize="lg" color={textColor} fontWeight="600" mb={4}>
            {fileNameOnly}
          </Text>
          <audio
            controls
            style={{ width: '100%', maxWidth: '500px' }}
            preload="metadata"
          >
            <source src={fileUrl} type={`audio/${fileFormat}`} />
            Tu navegador no soporta la reproducción de audio.
          </audio>
        </Box>
      );
    }
    // Video files - render con elemento video nativo
    else if (['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v'].includes(fileFormat)) {
      return (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          overflow="hidden"
          bg={imageContainerBg}
          p={4}
          textAlign="center"
        >
          <video
            controls
            style={{
              width: '100%',
              maxHeight: isVerticalLayout ? '180px' :
                        parallelMode ? '350px' : '600px'
            }}
            preload="metadata"
          >
            <source src={fileUrl} type={`video/${fileFormat}`} />
            Tu navegador no soporta la reproducción de video.
          </video>
        </Box>
      );
    }
    // Código y texto plano - usar iframe para renderizado nativo
    else if (['js', 'css', 'php', 'py', 'java', 'cpp', 'c', 'h', 'sql', 'md', 'yaml', 'yml', 'log'].includes(fileFormat)) {
      return (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="15px"
          overflow="hidden"
          bg={imageContainerBg}
        >
          <iframe
            src={fileUrl}
            width="100%"
            height={
              isVerticalLayout ? "200px" :
              parallelMode ? "450px" : "600px"
            }
            title={fileNameOnly}
            style={{ border: 'none', fontFamily: 'monospace' }}
          />
        </Box>
      );
    } else {
      return (
        <Box
          p={8}
          bg={imageContainerBg}
          borderRadius="15px"
          border="2px dashed"
          borderColor={fallbackBorderColor}
          textAlign="center"
        >
          <Icon as={FileIcon} boxSize={16} color={color} mb={4} />
          <Text fontSize="lg" color={textColor} fontWeight="600" mb={2}>
            Vista previa no disponible
          </Text>
          <Text fontSize="sm" color="gray.500" mb={6}>
            Este tipo de archivo no se puede visualizar en el navegador.
          </Text>
          <Button
            as="a"
            href={fileUrl}
            download={fileNameOnly}
            colorScheme="blue"
            leftIcon={<FaDownload />}
          >
            Descargar para ver
          </Button>
        </Box>
      );
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileNameOnly;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isVerticalLayout ? "full" : (parallelMode ? "4xl" : "6xl")}
      closeOnOverlayClick={!parallelMode}
      blockScrollOnMount={false}
    >
      <ModalOverlay bg={parallelMode ? "transparent" : "blackAlpha.600"} />
      <ModalContent
        maxW={
          isVerticalLayout ? "95vw" :
          parallelMode ? "45vw" : "90vw"
        }
        maxH="90vh"
        ml={
          isVerticalLayout ? "auto" :
          parallelMode ? "auto" : "auto"
        }
        mr={
          isVerticalLayout ? "auto" :
          parallelMode ? "2vw" : "auto"
        }
        position={
          isVerticalLayout ? "static" :
          parallelMode ? "fixed" : "relative"
        }
        right={
          isVerticalLayout ? "auto" :
          parallelMode ? "0" : "auto"
        }
        top={
          isVerticalLayout ? "auto" :
          parallelMode ? "5vh" : "auto"
        }
        left={isVerticalLayout ? "auto" : (parallelMode ? "auto" : "auto")}
        transform={
          isVerticalLayout ? "none" :
          parallelMode ? "none" : "translateX(-50%) translateY(-50%)"
        }
        zIndex={parallelMode ? 1500 : 1400}
        mt={isVerticalLayout ? "4vh" : "auto"}
        mb={isVerticalLayout ? "4vh" : "auto"}
        mx={isVerticalLayout ? "2.5vw" : "auto"}
        overflow="hidden"
      >
        <ModalHeader>
          <VStack align="start" spacing={2}>
            {/* Indicador de modo paralelo */}
            {parallelMode && (
              <Box
                bg="green.50"
                border="1px solid"
                borderColor="green.200"
                borderRadius="8px"
                px={3}
                py={1}
                alignSelf="stretch"
              >
                <Text fontSize="xs" color="green.700" fontWeight="600" textAlign="center">
                  📄 Vista paralela - Survey Response {isVerticalLayout ? '↑ ↓' : '← →'} Archivo
                </Text>
              </Box>
            )}

            <HStack spacing={4}>
              <Box
                p={3}
                bg={color}
                borderRadius="12px"
                color="white"
              >
                <Icon as={FileIcon} boxSize={6} />
              </Box>
              <VStack align="start" spacing={1}>
                <Text fontSize={parallelMode ? "lg" : "xl"} fontWeight="bold" color={textColor} noOfLines={1}>
                  {fileNameOnly}
                </Text>
                <HStack spacing={4}>
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                    {fileFormat.toUpperCase()}
                  </Badge>
                  <HStack spacing={2}>
                    <Icon as={FaCalendarAlt} color="gray.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.500">
                      {createdAt}
                    </Text>
                  </HStack>
                </HStack>
              </VStack>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody
          pb={6}
        >
          {renderFileContent()}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              colorScheme="green"
              leftIcon={<FaDownload />}
              onClick={handleDownload}
            >
              Descargar
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cerrar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocumentViewerModal;